// @ts-strict
import {type ReactElement} from 'react';
import {useFormikContext} from 'formik';
import useTranslation from 'next-translate/useTranslation';

import SelectField from '../../SelectField';
import TextField from '../../TextField';
import {type WebToCaseFormValues} from '../types';
import useSupportMessageOrderOptions from '../useSupportMessageOrderOptions';
import {
  categoryLevel2Path,
  getCategoryLevel1Options,
  getCategoryLevel2Label,
  getCategoryLevel2Options,
  isFieldVisible,
} from '../utils';

type Step2CategoryAndMessageProps = {
  // Step 1 owns this select on the wizard, so it is repeated here for context. The single-step
  // layout already shows step 1's fields, so it would be a duplicate there.
  hideCategoryLevel1?: boolean;
};

const Step2CategoryAndMessage = ({
  hideCategoryLevel1,
}: Step2CategoryAndMessageProps): ReactElement => {
  const {t} = useTranslation('common');
  const {values} = useFormikContext<WebToCaseFormValues>();
  const showOrderNumbers = isFieldVisible('orderNumbers', values);
  const orderOptions = useSupportMessageOrderOptions({skip: !showOrderNumbers});

  return (
    <div className="flex flex-col gap-4" data-testid="support-message-step-2">
      {!hideCategoryLevel1 && (
        <SelectField
          name="categoryLevel1"
          label={t('components.SupportMessage.fields.categoryLevel1')}
          options={getCategoryLevel1Options(t)}
          placeholder={t('components.SupportMessage.placeholders.none')}
          testId="support-message-categoryLevel1"
        />
      )}
      <SelectField
        name={categoryLevel2Path(values.categoryLevel1)}
        fieldId="categoryLevel2"
        testId="support-message-categoryLevel2"
        disabled={!values.categoryLevel1}
        label={getCategoryLevel2Label(values.categoryLevel1, t)}
        options={getCategoryLevel2Options(values.categoryLevel1, t)}
        placeholder={t('components.SupportMessage.placeholders.selectOne')}
      />
      {showOrderNumbers && (
        <SelectField
          name="orderNumbers"
          label={t('components.SupportMessage.fields.orderNumbers')}
          options={orderOptions}
          placeholder={t('components.SupportMessage.placeholders.none')}
          testId="support-message-orderNumbers"
        />
      )}
      <TextField
        name="description"
        label={t('components.SupportMessage.fields.description')}
        placeholder={t('components.SupportMessage.placeholders.description')}
        type="textarea"
        maxLength={1000}
        testId="support-message-description"
      />
    </div>
  );
};

export default Step2CategoryAndMessage;
